import { apiService } from './api.service';
import { API_ENDPOINTS } from '@shared/constants';

// Backend response types
interface BackendWorkShift {
  id: number;
  name: string;
  code: string;
  checkInTime: string;
  checkOutTime: string;
  lunchBreakStart?: string;
  lunchBreakEnd?: string;
  overtimeStartTime?: string;
  overtimeEndTime?: string;
  standardWorkingHours?: number;
  isActive: boolean;
  applyToAllEmployees: boolean;
  applicableUsers?: Array<{ id: number; fullName: string; employeeCode: string }>;
  applicableDepartments?: Array<{ id: number; name: string; code: string }>;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

interface BackendGpsLocation {
  id: number;
  name: string;
  address?: string;
  latitude: number;
  longitude: number;
  radius?: number;
  allowOutsideRadius: boolean;
  isActive: boolean;
  applyToAllEmployees: boolean;
  applicableUsers?: Array<{ id: number; fullName: string; employeeCode: string }>;
  createdAt: string;
  updatedAt: string;
}

interface BackendMealAllowanceConfig {
  id: number;
  minimumWorkingHours: number;
  defaultMealAllowance: number;
  isActive: boolean;
  description?: string;
  updatedAt: string;
  createdAt: string;
}

interface BackendCompanyInfo {
  id?: number;
  companyName: string;
  address?: string | null;
  taxCode?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  website?: string | null;
  logo?: string | null;
  description?: string | null;
  createdAt?: Date;
  updatedAt?: Date;
}

interface BackendResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface BackendListResponse<T> {
  success: boolean;
  data: T[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Frontend types
export interface WorkShift {
  id: string;
  name: string;
  enabled: boolean;
  checkInTime?: string;
  checkOutTime?: string;
  lunchBreakStart?: string;
  lunchBreakEnd?: string;
  standardWorkingHours?: number;
  code?: string;
  applyToAllEmployees?: boolean;
  applicableUserIds?: number[];
  applicableDepartmentIds?: number[];
}

export interface GPSPoint {
  id: string;
  locationName: string;
  address: string;
  latitude: string;
  longitude: string;
  radius: number;
  allowOutsideRadius: boolean;
  applicableScope: string;
  selectedEmployeeIds?: string[];
  enabled: boolean;
  isActive?: boolean;
  applyToAllEmployees?: boolean;
}

export interface MealAllowanceConfig {
  minimumHours: number;
  defaultMealAllowance?: number;
}

export interface CompanyInfo {
  companyName: string;
  address: string;
  taxCode: string;
  phoneNumber: string;
}

export interface Employee {
  id: number;
  name: string;
  employeeCode: string;
  departmentId: number;
  avatar: string | null;
}

export interface DepartmentWithEmployees {
  id: number;
  name: string;
  employees: Employee[];
}

class SystemSettingsService {
  // Work Shifts
  async getWorkShifts(): Promise<WorkShift[]> {
    const response = await apiService.get<BackendListResponse<BackendWorkShift>>(
      API_ENDPOINTS.WORK_SHIFTS.LIST
    );

    return response.data.map((shift) => ({
      id: String(shift.id),
      name: shift.name,
      enabled: shift.isActive,
      checkInTime: shift.checkInTime,
      checkOutTime: shift.checkOutTime,
      lunchBreakStart: shift.lunchBreakStart,
      lunchBreakEnd: shift.lunchBreakEnd,
      standardWorkingHours: shift.standardWorkingHours,
      code: shift.code,
      applyToAllEmployees: shift.applyToAllEmployees,
      applicableUserIds: shift.applicableUsers?.map((u) => u.id),
      applicableDepartmentIds: shift.applicableDepartments?.map((d) => d.id),
    }));
  }

  async createWorkShift(shift: Omit<WorkShift, 'id'>): Promise<WorkShift> {
    // Đảm bảo standardWorkingHours là number hoặc undefined
    let standardWorkingHours: number | undefined = shift.standardWorkingHours;
    if (standardWorkingHours !== undefined && standardWorkingHours !== null) {
      standardWorkingHours = Number(standardWorkingHours);
      if (isNaN(standardWorkingHours)) {
        standardWorkingHours = undefined;
      }
    }

    // Đảm bảo applyToAllEmployees có giá trị mặc định
    const applyToAllEmployees = shift.applyToAllEmployees ?? true;
    
    const payload: any = {
      name: shift.name,
      code: shift.code || shift.name.toLowerCase().replace(/\s+/g, '-'),
      checkInTime: this.formatTime(shift.checkInTime),
      checkOutTime: this.formatTime(shift.checkOutTime),
      lunchBreakStart: this.formatTime(shift.lunchBreakStart),
      lunchBreakEnd: this.formatTime(shift.lunchBreakEnd),
      standardWorkingHours: standardWorkingHours,
      isActive: shift.enabled,
      applyToAllEmployees: applyToAllEmployees,
    };

    // Chỉ gửi applicableUserIds nếu không áp dụng cho tất cả
    if (!applyToAllEmployees && shift.applicableUserIds && shift.applicableUserIds.length > 0) {
      payload.applicableUserIds = shift.applicableUserIds;
    }

    // Chỉ gửi applicableDepartmentIds nếu không áp dụng cho tất cả
    if (!applyToAllEmployees && shift.applicableDepartmentIds && shift.applicableDepartmentIds.length > 0) {
      payload.applicableDepartmentIds = shift.applicableDepartmentIds;
    }

    const response = await apiService.post<BackendResponse<BackendWorkShift>>(
      API_ENDPOINTS.WORK_SHIFTS.CREATE,
      payload
    );

    return {
      id: String(response.data.id),
      name: response.data.name,
      enabled: response.data.isActive,
      checkInTime: response.data.checkInTime,
      checkOutTime: response.data.checkOutTime,
      lunchBreakStart: response.data.lunchBreakStart,
      lunchBreakEnd: response.data.lunchBreakEnd,
      standardWorkingHours: response.data.standardWorkingHours,
      code: response.data.code,
      applyToAllEmployees: response.data.applyToAllEmployees,
      applicableUserIds: response.data.applicableUsers?.map((u) => u.id),
      applicableDepartmentIds: response.data.applicableDepartments?.map((d) => d.id),
    };
  }

  // Helper function để format thời gian thành HH:mm
  private formatTime(time: string | undefined): string | undefined {
    if (!time) return undefined;
    const [h, m] = time.split(':');
    if (h && m) {
      return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
    }
    return time;
  }

  async updateWorkShift(id: string, shift: Partial<WorkShift>): Promise<WorkShift> {
    const payload: any = {};
    if (shift.name !== undefined) payload.name = shift.name;
    if (shift.checkInTime !== undefined) payload.checkInTime = this.formatTime(shift.checkInTime);
    if (shift.checkOutTime !== undefined) payload.checkOutTime = this.formatTime(shift.checkOutTime);
    if (shift.lunchBreakStart !== undefined) payload.lunchBreakStart = this.formatTime(shift.lunchBreakStart);
    if (shift.lunchBreakEnd !== undefined) payload.lunchBreakEnd = this.formatTime(shift.lunchBreakEnd);
    
    // Đảm bảo standardWorkingHours là number
    if (shift.standardWorkingHours !== undefined && shift.standardWorkingHours !== null) {
      const numValue = Number(shift.standardWorkingHours);
      if (!isNaN(numValue)) {
        payload.standardWorkingHours = numValue;
      }
    }
    
    if (shift.enabled !== undefined) payload.isActive = shift.enabled;
    if (shift.applyToAllEmployees !== undefined) payload.applyToAllEmployees = shift.applyToAllEmployees;
    
    // Chỉ gửi applicableUserIds nếu không áp dụng cho tất cả
    if (shift.applyToAllEmployees === false && shift.applicableUserIds && shift.applicableUserIds.length > 0) {
      payload.applicableUserIds = shift.applicableUserIds;
    } else if (shift.applyToAllEmployees === true) {
      // Nếu áp dụng cho tất cả, không gửi applicableUserIds
      payload.applyToAllEmployees = true;
    }

    // Chỉ gửi applicableDepartmentIds nếu không áp dụng cho tất cả
    if (shift.applyToAllEmployees === false && shift.applicableDepartmentIds && shift.applicableDepartmentIds.length > 0) {
      payload.applicableDepartmentIds = shift.applicableDepartmentIds;
    }

    const response = await apiService.put<BackendResponse<BackendWorkShift>>(
      API_ENDPOINTS.WORK_SHIFTS.UPDATE(id),
      payload
    );

    return {
      id: String(response.data.id),
      name: response.data.name,
      enabled: response.data.isActive,
      checkInTime: response.data.checkInTime,
      checkOutTime: response.data.checkOutTime,
      lunchBreakStart: response.data.lunchBreakStart,
      lunchBreakEnd: response.data.lunchBreakEnd,
      standardWorkingHours: response.data.standardWorkingHours,
      code: response.data.code,
      applyToAllEmployees: response.data.applyToAllEmployees,
      applicableUserIds: response.data.applicableUsers?.map((u) => u.id),
      applicableDepartmentIds: response.data.applicableDepartments?.map((d) => d.id),
    };
  }

  async deleteWorkShift(id: string): Promise<void> {
    await apiService.delete(API_ENDPOINTS.WORK_SHIFTS.DELETE(id));
  }

  async activateWorkShift(id: string): Promise<WorkShift> {
    const response = await apiService.put<BackendResponse<BackendWorkShift>>(
      API_ENDPOINTS.WORK_SHIFTS.ACTIVATE(id)
    );

    return {
      id: String(response.data.id),
      name: response.data.name,
      enabled: response.data.isActive,
      checkInTime: response.data.checkInTime,
      checkOutTime: response.data.checkOutTime,
      lunchBreakStart: response.data.lunchBreakStart,
      lunchBreakEnd: response.data.lunchBreakEnd,
      standardWorkingHours: response.data.standardWorkingHours,
      code: response.data.code,
      applyToAllEmployees: response.data.applyToAllEmployees,
      applicableUserIds: response.data.applicableUsers?.map((u) => u.id),
    };
  }

  // GPS Locations
  async getGpsLocations(): Promise<GPSPoint[]> {
    const response = await apiService.get<BackendListResponse<BackendGpsLocation>>(
      API_ENDPOINTS.GPS_LOCATIONS.LIST
    );

    return response.data.map((location) => ({
      id: String(location.id),
      locationName: location.name,
      address: location.address || '',
      latitude: String(location.latitude),
      longitude: String(location.longitude),
      radius: location.radius || 100,
      allowOutsideRadius: location.allowOutsideRadius,
      applicableScope: location.applyToAllEmployees ? 'all' : 'specific',
      selectedEmployeeIds: location.applicableUsers?.map((u) => String(u.id)),
      enabled: location.isActive,
      isActive: location.isActive,
      applyToAllEmployees: location.applyToAllEmployees,
    }));
  }

  async createGpsLocation(location: Omit<GPSPoint, 'id'>): Promise<GPSPoint> {
    const payload = {
      name: location.locationName,
      address: location.address,
      latitude: parseFloat(location.latitude),
      longitude: parseFloat(location.longitude),
      radius: location.radius,
      allowOutsideRadius: location.allowOutsideRadius,
      isActive: location.enabled ?? true,
      applyToAllEmployees: location.applicableScope === 'all',
      applicableUserIds:
        location.applicableScope === 'specific' && location.selectedEmployeeIds
          ? location.selectedEmployeeIds.map((id) => parseInt(id, 10))
          : undefined,
    };

    const response = await apiService.post<BackendResponse<BackendGpsLocation>>(
      API_ENDPOINTS.GPS_LOCATIONS.CREATE,
      payload
    );

    return {
      id: String(response.data.id),
      locationName: response.data.name,
      address: response.data.address || '',
      latitude: String(response.data.latitude),
      longitude: String(response.data.longitude),
      radius: response.data.radius || 100,
      allowOutsideRadius: response.data.allowOutsideRadius,
      applicableScope: response.data.applyToAllEmployees ? 'all' : 'specific',
      selectedEmployeeIds: response.data.applicableUsers?.map((u) => String(u.id)),
      enabled: response.data.isActive,
      isActive: response.data.isActive,
      applyToAllEmployees: response.data.applyToAllEmployees,
    };
  }

  async updateGpsLocation(id: string, location: Partial<GPSPoint>): Promise<GPSPoint> {
    const payload: any = {};
    if (location.locationName !== undefined) payload.name = location.locationName;
    if (location.address !== undefined) payload.address = location.address;
    if (location.latitude !== undefined) payload.latitude = parseFloat(location.latitude);
    if (location.longitude !== undefined) payload.longitude = parseFloat(location.longitude);
    if (location.radius !== undefined) payload.radius = location.radius;
    if (location.allowOutsideRadius !== undefined) payload.allowOutsideRadius = location.allowOutsideRadius;
    if (location.applicableScope !== undefined) {
      payload.applyToAllEmployees = location.applicableScope === 'all';
      if (location.applicableScope === 'specific' && location.selectedEmployeeIds) {
        payload.applicableUserIds = location.selectedEmployeeIds.map((id) => parseInt(id, 10));
      }
    }
    if (location.enabled !== undefined) {
      payload.isActive = location.enabled;
    } else if (location.isActive !== undefined) {
      payload.isActive = location.isActive;
    }

    const response = await apiService.put<BackendResponse<BackendGpsLocation>>(
      API_ENDPOINTS.GPS_LOCATIONS.UPDATE(id),
      payload
    );

    return {
      id: String(response.data.id),
      locationName: response.data.name,
      address: response.data.address || '',
      latitude: String(response.data.latitude),
      longitude: String(response.data.longitude),
      radius: response.data.radius || 100,
      allowOutsideRadius: response.data.allowOutsideRadius,
      applicableScope: response.data.applyToAllEmployees ? 'all' : 'specific',
      selectedEmployeeIds: response.data.applicableUsers?.map((u) => String(u.id)),
      enabled: response.data.isActive,
      isActive: response.data.isActive,
      applyToAllEmployees: response.data.applyToAllEmployees,
    };
  }

  async deleteGpsLocation(id: string): Promise<void> {
    await apiService.delete(API_ENDPOINTS.GPS_LOCATIONS.DELETE(id));
  }

  async activateGpsLocation(id: string): Promise<GPSPoint> {
    const response = await apiService.put<BackendResponse<BackendGpsLocation>>(
      API_ENDPOINTS.GPS_LOCATIONS.ACTIVATE(id)
    );

    return {
      id: String(response.data.id),
      locationName: response.data.name,
      address: response.data.address || '',
      latitude: String(response.data.latitude),
      longitude: String(response.data.longitude),
      radius: response.data.radius || 100,
      allowOutsideRadius: response.data.allowOutsideRadius,
      applicableScope: response.data.applyToAllEmployees ? 'all' : 'specific',
      selectedEmployeeIds: response.data.applicableUsers?.map((u) => String(u.id)),
      enabled: response.data.isActive,
      isActive: response.data.isActive,
      applyToAllEmployees: response.data.applyToAllEmployees,
    };
  }

  // Meal Allowance Config
  async getMealAllowanceConfig(): Promise<MealAllowanceConfig> {
    const response = await apiService.get<BackendResponse<BackendMealAllowanceConfig>>(
      API_ENDPOINTS.MEAL_ALLOWANCE_CONFIG.GET
    );

    return {
      minimumHours: response.data.minimumWorkingHours,
      defaultMealAllowance: response.data.defaultMealAllowance,
    };
  }

  async updateMealAllowanceConfig(config: MealAllowanceConfig): Promise<MealAllowanceConfig> {
    const payload = {
      minimumWorkingHours: config.minimumHours,
      defaultMealAllowance: config.defaultMealAllowance,
    };

    const response = await apiService.put<BackendResponse<BackendMealAllowanceConfig>>(
      API_ENDPOINTS.MEAL_ALLOWANCE_CONFIG.UPDATE,
      payload
    );

    return {
      minimumHours: response.data.minimumWorkingHours,
      defaultMealAllowance: response.data.defaultMealAllowance,
    };
  }

  // Company Info
  async getCompanyInfo(): Promise<CompanyInfo | null> {
    try {
      const response = await apiService.get<BackendResponse<BackendCompanyInfo>>(
        API_ENDPOINTS.COMPANIES.GET
      );
      // Map từ backend response (có thể có nullable fields) sang frontend format
      return {
        companyName: response.data.companyName || '',
        address: response.data.address || '',
        taxCode: response.data.taxCode || '',
        phoneNumber: response.data.phoneNumber || '',
      };
    } catch (error: any) {
      // Nếu chưa có company info (404), trả về null
      if (error?.response?.status === 404 || error?.message?.includes('chưa được thiết lập')) {
        return null;
      }
      throw error;
    }
  }

  async createCompanyInfo(companyInfo: CompanyInfo): Promise<CompanyInfo> {
    const response = await apiService.post<BackendResponse<BackendCompanyInfo>>(
      API_ENDPOINTS.COMPANIES.CREATE,
      companyInfo
    );
    // Map từ backend response (có thể có nullable fields) sang frontend format
    return {
      companyName: response.data.companyName || '',
      address: response.data.address || '',
      taxCode: response.data.taxCode || '',
      phoneNumber: response.data.phoneNumber || '',
    };
  }

  async updateCompanyInfo(companyInfo: CompanyInfo): Promise<CompanyInfo> {
    const response = await apiService.put<BackendResponse<BackendCompanyInfo>>(
      API_ENDPOINTS.COMPANIES.UPDATE,
      companyInfo
    );
    // Map từ backend response (có thể có nullable fields) sang frontend format
    return {
      companyName: response.data.companyName || '',
      address: response.data.address || '',
      taxCode: response.data.taxCode || '',
      phoneNumber: response.data.phoneNumber || '',
    };
  }

  async saveCompanyInfo(companyInfo: CompanyInfo, isNew: boolean = false): Promise<CompanyInfo> {
    if (isNew) {
      return this.createCompanyInfo(companyInfo);
    } else {
      // Thử update trước, nếu lỗi 404 thì fallback sang create
      try {
        return await this.updateCompanyInfo(companyInfo);
      } catch (error: any) {
        if (error?.response?.status === 404 || error?.message?.includes('chưa được thiết lập')) {
          return this.createCompanyInfo(companyInfo);
        }
        throw error;
      }
    }
  }

  // Departments with Employees
  async getDepartmentsWithEmployees(search?: string): Promise<DepartmentWithEmployees[]> {
    const queryParams = new URLSearchParams();
    if (search) {
      queryParams.append('search', search);
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${API_ENDPOINTS.DEPARTMENTS.WITH_EMPLOYEES}?${queryString}`
      : API_ENDPOINTS.DEPARTMENTS.WITH_EMPLOYEES;

    const response = await apiService.get<BackendResponse<DepartmentWithEmployees[]>>(endpoint);
    return response.data;
  }
}

export const systemSettingsService = new SystemSettingsService();

